import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  LogOut,
  Settings,
  HelpCircle,
  Shield,
  Clock,
  ChevronDown,
  Mail,
  Building,
  Database,
  Sun,
  Moon,
  Monitor,
  Scale
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { useTheme } from '../../../contexts/ThemeContext';

const UserDropdown: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { hasPermission } = usePermissions();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isAuthenticated || !user) {
    return null;
  }



  const formatLastLogin = (date?: Date) => {
    if (!date) return 'Never';
    const now = new Date();
    const lastLogin = new Date(date);
    const diffInHours = Math.floor((now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return 'Yesterday';
    return lastLogin.toLocaleDateString();
  };

  const handleLogout = async () => {
    try {
      // Call backend logout endpoint
      const token = localStorage.getItem('authToken');
      if (token) {
        await fetch(`${process.env.REACT_APP_API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      logout();
      navigate('/login');
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Elegant Dropdown Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-white/60 dark:hover:bg-dark-surface-raised/60 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-accent-400/20 backdrop-blur-sm group"
      >
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 dark:from-accent-400 dark:via-accent-500 dark:to-accent-600 rounded-xl flex items-center justify-center text-white font-medium shadow-sm">
            {user.firstName.charAt(0)}{user.lastName.charAt(0)}
          </div>
          <div className="text-left hidden sm:block">
            <div className="text-sm font-medium text-gray-800 dark:text-white">
              {user.firstName} {user.lastName}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{user.email}</div>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-all duration-200 group-hover:text-gray-700 dark:group-hover:text-gray-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Elegant Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-3 w-80 backdrop-blur-xl bg-white/95 dark:bg-dark-surface/95 rounded-2xl shadow-2xl dark:shadow-dark-lg border border-gray-200 dark:border-dark-border z-50 overflow-hidden flex flex-col max-h-[calc(100vh-6rem)]">
          {/* Elegant User Info Section */}
          <div className="flex-shrink-0 p-6 bg-gradient-to-r from-primary-50/50 to-primary-100/50 dark:from-primary-900/20 dark:to-primary-800/20 backdrop-blur-sm">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 dark:from-accent-400 dark:via-accent-500 dark:to-accent-600 rounded-2xl flex items-center justify-center text-white font-medium text-xl shadow-lg">
                {user.firstName.charAt(0)}{user.lastName.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-800 dark:text-white text-lg truncate">
                  {user.firstName} {user.lastName}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2 mt-1">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate" title={user.email}>{user.email}</span>
                </p>
                {user.department && (
                  <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2 mt-1">
                    <Building className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{user.department}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Elegant Role and Status */}
            <div className="flex items-center justify-between mt-5">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white/60 dark:bg-dark-bg-secondary/60 rounded-xl border border-gray-200 dark:border-dark-border">
                <Shield className="w-4 h-4 text-primary-500 dark:text-accent-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 capitalize">{user.role}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Clock className="w-4 h-4" />
                <span>{formatLastLogin(user.lastLogin)}</span>
              </div>
            </div>

            {/* Theme Toggle */}
            <div className="mt-5 pt-4 border-t border-gray-200/50 dark:border-dark-border/50">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Appearance</span>
                <div className="flex items-center gap-1 p-1 bg-white/60 dark:bg-dark-bg-secondary/60 rounded-xl border border-gray-200 dark:border-dark-border">
                  <button
                    onClick={() => setTheme('light')}
                    className={`p-2 rounded-lg transition-all duration-200 ${
                      theme === 'light'
                        ? 'bg-primary-100 dark:bg-accent-900/50 text-primary-600 dark:text-accent-400 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-surface-raised'
                    }`}
                    title="Light mode"
                  >
                    <Sun className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setTheme('dark')}
                    className={`p-2 rounded-lg transition-all duration-200 ${
                      theme === 'dark'
                        ? 'bg-primary-100 dark:bg-accent-900/50 text-primary-600 dark:text-accent-400 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-surface-raised'
                    }`}
                    title="Dark mode"
                  >
                    <Moon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setTheme('system')}
                    className={`p-2 rounded-lg transition-all duration-200 ${
                      theme === 'system'
                        ? 'bg-primary-100 dark:bg-accent-900/50 text-primary-600 dark:text-accent-400 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-surface-raised'
                    }`}
                    title="System preference"
                  >
                    <Monitor className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Menu items take the remaining height and scroll internally, so a
              long menu shortens this region instead of pushing Sign Out off-screen. */}
          <div className="flex-1 min-h-0 overflow-y-auto p-3">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/settings/profile');
              }}
              className="w-full group px-4 py-3 text-left rounded-xl hover:bg-gray-100 dark:hover:bg-dark-bg-secondary transition-all duration-200 flex items-center gap-3"
            >
              <div className="p-2 bg-primary-50 dark:bg-primary-900/30 text-primary-500 dark:text-primary-400 rounded-lg group-hover:bg-primary-100 dark:group-hover:bg-primary-900/50">
                <User className="w-4 h-4" />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">My Profile</span>
                <p className="text-xs text-gray-500 dark:text-gray-400">View and edit your profile</p>
              </div>
            </button>

            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/settings/account');
              }}
              className="w-full group px-4 py-3 text-left rounded-xl hover:bg-gray-100 dark:hover:bg-dark-bg-secondary transition-all duration-200 flex items-center gap-3 mt-2"
            >
              <div className="p-2 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-lg group-hover:bg-primary-100 dark:group-hover:bg-primary-900/50">
                <Settings className="w-4 h-4" />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Account Settings</span>
                <p className="text-xs text-gray-500 dark:text-gray-400">Manage your preferences</p>
              </div>
            </button>

            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/help');
              }}
              className="w-full group px-4 py-3 text-left rounded-xl hover:bg-gray-100 dark:hover:bg-dark-bg-secondary transition-all duration-200 flex items-center gap-3 mt-2"
            >
              <div className="p-2 bg-accent-50 dark:bg-accent-900/30 text-accent-500 dark:text-accent-400 rounded-lg group-hover:bg-accent-100 dark:group-hover:bg-accent-900/50">
                <HelpCircle className="w-4 h-4" />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Help & Support</span>
                <p className="text-xs text-gray-500 dark:text-gray-400">Get assistance and docs</p>
              </div>
            </button>

            {/* Legal AI Assistant */}
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/legal-ai');
              }}
              className="w-full group px-4 py-3 text-left rounded-xl hover:bg-gray-100 dark:hover:bg-dark-bg-secondary transition-all duration-200 flex items-center gap-3 mt-2"
            >
              <div className="p-2 bg-purple-50 dark:bg-purple-900/30 text-purple-500 dark:text-purple-400 rounded-lg group-hover:bg-purple-100 dark:group-hover:bg-purple-900/50">
                <Scale className="w-4 h-4" />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Legal AI Assistant</span>
                <p className="text-xs text-gray-500 dark:text-gray-400">AI-powered contract analysis</p>
              </div>
            </button>

            {/* Admin-only options */}
            {(hasPermission('masterdata.view') || hasPermission('users.view')) && (
              <>
                <div className="border-t border-gray-200 dark:border-dark-border my-4"></div>
                <div className="space-y-2">
                  {/* Master Data */}
                  {hasPermission('masterdata.view') && (
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        navigate('/master-data');
                      }}
                      className="w-full group px-4 py-3 text-left rounded-xl hover:bg-gray-100 dark:hover:bg-dark-bg-secondary transition-all duration-200 flex items-center gap-3"
                    >
                      <div className="p-2 bg-purple-50 dark:bg-purple-900/30 text-purple-500 dark:text-purple-400 rounded-lg group-hover:bg-purple-100 dark:group-hover:bg-purple-900/50">
                        <Database className="w-4 h-4" />
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Master Data</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Manage system data</p>
                      </div>
                    </button>
                  )}

                  {/* Admin Dashboard */}
                  {hasPermission('users.view') && (
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        navigate('/admin');
                      }}
                      className="w-full group px-4 py-3 text-left rounded-xl hover:bg-gray-100 dark:hover:bg-dark-bg-secondary transition-all duration-200 flex items-center gap-3 mt-2"
                    >
                      <div className="p-2 bg-error-50 dark:bg-error-900/30 text-error-500 dark:text-error-400 rounded-lg group-hover:bg-error-100 dark:group-hover:bg-error-900/50">
                        <Shield className="w-4 h-4" />
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Admin Dashboard</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">User and system management</p>
                      </div>
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Logout - flex-shrink-0 keeps it pinned and always reachable, even
              when the sections above overflow and scroll. */}
          <div className="flex-shrink-0 border-t border-gray-200 dark:border-dark-border p-3 bg-white/95 dark:bg-dark-surface/95">
            <button
              onClick={handleLogout}
              className="w-full group px-4 py-3 text-left rounded-xl hover:bg-error-50 dark:hover:bg-error-900/30 transition-all duration-200 flex items-center gap-3"
            >
              <div className="p-2 bg-error-50 dark:bg-error-900/30 text-error-500 dark:text-error-400 rounded-lg group-hover:bg-error-100 dark:group-hover:bg-error-900/50">
                <LogOut className="w-4 h-4" />
              </div>
              <div>
                <span className="text-sm font-medium text-error-600 dark:text-error-400">Sign Out</span>
                <p className="text-xs text-error-400 dark:text-error-500">End your session securely</p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDropdown; 